import React, { useState } from 'react';
import { TouchableOpacity, Text, StyleSheet, Platform, View, ActivityIndicator } from 'react-native';
import { useTheme } from '../../../theme/ThemeProvider';
import { globalConfigurableData, GlobalScreens, partnerMap } from '../../../utils/globalConstant';
import { globalFontStyle } from '../../../assets/Font/FontFamily';
import FastImage from '../../../common/FastImage';
import { convert } from "../../../locale/translateText";
import Colors from '../../../assets/Color/Colors';
import { isMobile } from '../../../utils/commonMethods';
import { smile_coin_yes, smile_coin_no } from '../../../utils/images';
import './Button.web.css';

const ButtonConfig = {
  dimensions: {
    minWidth: 80,
    borderRadius: 6,
    gap: 8,
    iconSizes: {
      sm: 14,
      default: 16,
      lg: 18,
      icon: 24,
    }
  },
  spacing: {
    marginTop: 4,
    marginRight: 8,
    marginLeft: 8,
  },
  opacity: {
    disabled: 0.5,
    pressed: 0.8,
    loading: 0.8,
    inactive: 0.6,
    active: 1,
  },
  shadow: {
    color: '#000',
    offset: { width: 0, height: 1 },
    opacity: 0.1,
    radius: 2,
  },
  fallbackColors: {
    text: '#000000',
    background: '#FFFFFF',
  }
};

/**
 * Enhanced Button Component
 * @param {Object} props
 * @param {'primary' | 'secondary' | 'outline' | 'yes' | 'no' | 'yes-inactive' | 'no-inactive' | 'black' | 'grey' | 'yellow' | 'red' | 'rm-liquidity' | 'merge-share' | 'black-bordered' | 'black-white' | 'icon'} props.variant - Button variant
 * @param {'small' | 'medium' | 'large' | 'icon'} props.size - Button size
 * @param {Function} props.onPress - Button press handler
 * @param {boolean} props.disabled - Disabled state
 * @param {React.ReactNode} props.children - Button content
 * @param {Object} props.style - Additional style overrides
 * @param {Object} props.textStyle - Additional text style overrides
 * @param {boolean} props.translate - Whether to translate the button text
 * @param {boolean} props.isImageInBtn - Whether the button contains an image
 * @param {Function} props.leftIcon - Function that returns the left icon source
 * @param {Function} props.rightIcon - Function that returns the right icon source
 * @param {string} props.subText - Sub text to display below main text
 * @param {Object} props.subTextStyle - Additional sub text style overrides
 * @param {boolean} props.isSmileEvent - Whether this is a smile event button
 * @param {Object} props.returnsVar - Returns variable for smile event
 * @param {number} props.delay - Delay in milliseconds before executing onPress
 * @param {number} props.delayPressIn - Delay before executing press in
 * @param {string} props.className - CSS class name for web
 * @param {number} props.activeOpacity - Opacity when pressed
 * @param {boolean} props.loading - Whether the button is in loading state
 * @param {string} props.loadingText - Optional loading text
 * @param {string} props.loadingColor - Optional loading color override
 * @param {string} props.loadingSize - Size of loading indicator
 * @param {any} props.icon - Icon source for icon variant
 * @param {Object} props.iconStyle - Style overrides for icon
 * @param {number} props.iconSize - Size of the icon
 */
export const Button = ({
  variant = 'primary',
  size = 'default',
  onPress,
  disabled,
  children,
  style,
  textStyle,
  translate = false,
  isImageInBtn = false,
  leftIcon,
  rightIcon,
  subText,
  subTextStyle,
  isSmileEvent = false,
  returnsVar,
  delay = 0,
  delayPressIn = 0,
  className = '',
  activeOpacity = 0.8,
  loading = false,
  loadingText,
  loadingColor,
  loadingSize = 'small',
  icon,
  iconStyle,
  iconSize = 24,
  backgroundColor,
  ...props
}) => {
  const theme = useTheme();
  const [pressed, setPressed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const getBaseStyles = () => ({
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: ButtonConfig.dimensions.gap,
    
    minWidth: ButtonConfig.dimensions.minWidth,
    borderRadius: ButtonConfig.dimensions.borderRadius,
    
    opacity: disabled ? ButtonConfig.opacity.disabled : ButtonConfig.opacity.active,
    
    ...Platform.select({
      web: {
        cursor: disabled ? 'not-allowed' : 'pointer',
      },
      default: {
        elevation: pressed ? 0 : 1,
        shadowColor: ButtonConfig.shadow.color,
        shadowOffset: ButtonConfig.shadow.offset,
        shadowOpacity: ButtonConfig.shadow.opacity,
        shadowRadius: ButtonConfig.shadow.radius,
      }
    }),
  });

  const getSizeStyles = () => {
    if (variant === 'icon') {
      return {
        width: 50,
        height: 50,
        padding: 0,
        borderRadius: 8,
        minWidth: 'auto',
      };
    }

    // Special case for trading buttons
    if (className?.includes('card-button')) {
      switch (size) {
        case 'sm':
          return {
            height: 32,
            paddingHorizontal: 12,
            ...globalFontStyle.appFontSmall,
          };
        case 'lg':
          return {
            height: 44,
            paddingHorizontal: 24,
            ...globalFontStyle.appFontLarge,
          };
        default:
          return {
            height: isMobile() ? 40 : 40,
            paddingHorizontal: 20,
            ...globalFontStyle.appFontMedium,
          };
      }
    }

    // Special case for auth buttons
    if (children === globalConfigurableData.authButtonText) {
      return {
        height: isMobile() ? 36 : 40,
        paddingHorizontal: 20,
        ...globalFontStyle.appFontMedium,
      };
    }

    // Default button sizes
    switch (size) {
      case 'sm':
        return {
          height: 32,
          paddingHorizontal: 12,
          ...globalFontStyle.appFontSmall,
        };
      case 'lg':
        return {
          height: 44,
          paddingHorizontal: 24,
          ...globalFontStyle.appFontLarge,
        };
      default:
        return {
          height: Platform.OS === 'web' ? (isMobile() ? 50 : 44) : 50,
          paddingHorizontal: 20,
          ...globalFontStyle.appFontMedium,
        };
    }
  };

  const getVariantStyles = () => {
    // Handle card buttons differently
    if (className?.includes('card-button')) {
      switch (variant) {
        case 'yes':
          return {
            backgroundColor: Colors.yesColorInactive,
            color: Colors.yesColor,
          };
        case 'no':
          return {
            backgroundColor: Colors.noColorInactive,
            color: Colors.noColor,
          };
      }
    }

    // Partner-specific overrides
    if (partnerMap?.isTmr) {
      switch (variant) {
        case 'yes':
          return {
            backgroundColor: disabled ? Colors.yesColorInactive : Colors.yesColor,
            color: disabled ? Colors.yesColor : Colors.bgWhite,
          };
        case 'no':
          return {
            backgroundColor: disabled ? Colors.noColorInactive : Colors.noColor,
            color: disabled ? Colors.noColor : Colors.bgWhite,
          };
        case 'yes-inactive':
          return {
            backgroundColor: Colors.yesColorInactive,
            color: Colors.yesColor,
          };
        case 'no-inactive':
          return {
            backgroundColor: Colors.noColorInactive,
            color: Colors.noColor,
          };
      }
    }

    // Default variants
    switch (variant) {
      case 'primary':
        return {
          backgroundColor: theme.colors.primary,
          color: theme.colors.background.primary || '#FFFFFF',
        };
      case 'secondary':
        return {
          backgroundColor: theme.colors.secondary,
          color: theme.colors.text.primary || '#000000',
        };
      case 'outline':
        return {
          backgroundColor: 'transparent',
          borderWidth: 1,
          borderColor: theme.colors.border,
          color: theme.colors.text.primary || '#000000',
        };
      case 'ghost':
        return {
          backgroundColor: 'transparent',
          color: theme.colors.text.primary || '#000000',
        };
      case 'destructive':
        return {
          backgroundColor: theme.colors.error,
          color: theme.colors.background.primary || '#FFFFFF',
        };
      case 'yes':
        return {
          backgroundColor: Colors.yesColor || '#26C037',
          color: '#FFFFFF',
        };
      case 'no':
        return {
          backgroundColor: Colors.noColor || '#CA2C92',
          color: '#FFFFFF',
        };
      case 'yes-inactive':
        return {
          backgroundColor: Colors.yesColorInactive || 'rgba(38, 92, 255, 0.1)',
          color: Colors.yesColor || 'rgb(38, 92, 255)',
        };
      case 'no-inactive':
        return {
          backgroundColor: Colors.noColorInactive || 'rgba(170, 0, 255, 0.1)',
          color: Colors.noColor || 'rgb(170, 0, 255)',
        };
      case 'black':
        return {
          backgroundColor: Colors.black || '#000000',
          color: '#FFFFFF',
        };
      case 'grey':
        return {
          backgroundColor: Colors.grey3 || '#E5E5E5',
          color: Colors.grey1 || '#999999',
        };
      case 'icon':
        return {
          backgroundColor: backgroundColor || theme.colors.primary,
          color: theme.colors.background.primary || '#FFFFFF',
          padding: 0,
          justifyContent: 'center',
          alignItems: 'center',
        };
      default:
        return {
          backgroundColor: theme.colors.primary || '#000000',
          color: theme.colors.background.primary || '#FFFFFF',
        };
    }
  };

  const styles = StyleSheet.create({
    button: {
      ...getBaseStyles(),
      ...getVariantStyles(),
      ...getSizeStyles(),
    },
    contentContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
    },
    icon: {
      width: size === 'sm' ? ButtonConfig.dimensions.iconSizes.sm 
           : size === 'lg' ? ButtonConfig.dimensions.iconSizes.lg 
           : ButtonConfig.dimensions.iconSizes.default,
      height: size === 'sm' ? ButtonConfig.dimensions.iconSizes.sm 
            : size === 'lg' ? ButtonConfig.dimensions.iconSizes.lg 
            : ButtonConfig.dimensions.iconSizes.default,
    },
    text: {
      color: getVariantStyles().color || '#000000',
      fontWeight: '500',
      textAlign: 'center',
      ...Platform.select({
        web: {
          whiteSpace: 'nowrap',
        }
      }),
    },
    subTextContainer: {
      marginTop: 4,
      opacity: 0.8,
    },
    loadingContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
    },
    loadingIndicator: {
      marginRight: 8,
    },
    loadingText: {
      ...globalFontStyle.appFontBody,
    },
    loading: {
      opacity: 0.8,
    },
    // Trading specific styles
    yes: {
      backgroundColor: Colors.yesColor,
    },
    no: {
      backgroundColor: Colors.noColor,
    },
    'yes-inactive': {
      backgroundColor: Colors.grey3,
    },
    'no-inactive': {
      backgroundColor: Colors.grey3,
    },
    disabled: {
      backgroundColor: Colors.grey3,
      opacity: 0.6,
    },
    disabledText: {
      color: Colors.grey1,
    },
  });

  const buttonClasses = [
    'button',
    `button-${variant}`,
    `button-${size}`,
    isLoading && 'button-loading',
    pressed && 'button-pressed',
    leftIcon || rightIcon ? 'button-icon' : '',
    className,
  ].filter(Boolean).join(' ');

  const handlePress = async (e) => {
    if (disabled || !onPress || isLoading) return;
    
    try {
      setIsLoading(true);
      
      if (delayPressIn) {
        await new Promise(resolve => setTimeout(resolve, delayPressIn));
      }

      if (delay) {
        await new Promise(resolve => setTimeout(resolve, delay));
      }

      await onPress(e);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePressIn = () => setPressed(true);
  const handlePressOut = () => setPressed(false);

  const renderContent = () => {
    if (variant === 'icon' && icon) {
      return (
        <FastImage
          source={icon}
          style={[
            {
              width: iconSize,
              height: iconSize,
              tintColor: iconStyle?.tintColor || getVariantStyles().color,
            },
            iconStyle,
          ]}
        />
      );
    }

    return (
      <>
        {typeof leftIcon === 'function' && (
          <FastImage source={leftIcon()} style={styles.icon} />
        )}
        {isImageInBtn ? children : (
          <Text style={[styles.text, textStyle]}>
            {translate ? convert(children) : children}
          </Text>
        )}
        {typeof rightIcon === 'function' && (
          <FastImage source={rightIcon()} style={styles.icon} />
        )}
      </>
    );
  };

  return (
    <TouchableOpacity
      style={[styles.button, style]}
      onPress={handlePress}
      onPressIn={handlePressIn}
      onPressOut={handlePressOut}
      disabled={disabled || isLoading}
      activeOpacity={activeOpacity}
      accessible={true}
      accessibilityRole="button"
      accessibilityState={{ 
        disabled,
        busy: isLoading,
      }}
      accessibilityLabel={typeof children === 'string' ? children : undefined}
      className={buttonClasses}
      data-pressed={pressed}
      {...props}
    >
      {loading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator
            color={loadingColor || getVariantStyles().color}
            size={loadingSize}
          />
          {loadingText && (
            <Text style={[styles.text, { marginLeft: 8 }]}>
              {loadingText}
            </Text>
          )}
        </View>
      ) : (
        renderContent()
      )}
    </TouchableOpacity>
  );
}; 